/**
 * kbResearch — pure, React-free helpers for the AI 'Knowledge Base Research'
 * capability on a task.
 *
 * {@link buildResearchQuestion} ENRICHES a task (its title, description, and
 * why/context) plus the NAMES of its linked goal / workstream / service into a
 * single, self-contained question for the team knowledge base
 * (RetrieveAndGenerate). The question explicitly asks for a MARKDOWN answer that
 * names the specific services/components/files likely to change, the concrete
 * steps, the risks, the testing needed, and cited sources — so the grounded
 * answer is directly actionable on the task.
 *
 * It is robust to missing fields: empty title/description/context/links are
 * simply omitted, never emitted as blanks, so a bare task still produces a
 * sensible question.
 *
 * Pure TS — no React, no api. Consumed by useTaskDetail.generateKbResearch.
 */

import type { Task } from './types';

/** Resolved NAMES of a task's optional linked entities (already looked up). */
export interface ResearchLinkNames {
  goalName?: string;
  workstreamName?: string;
  serviceName?: string;
}

/** Collapses whitespace and trims; returns '' for nullish input. */
function clean(value?: string): string {
  return (value ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Builds the enriched knowledge-base question for a task. Only the fields that
 * are actually present contribute to the prompt, so it stays coherent whether
 * the task is richly described or nearly empty.
 */
export function buildResearchQuestion(
  task: Pick<Task, 'title' | 'description' | 'context'>,
  links: ResearchLinkNames = {},
): string {
  const title = clean(task.title) || 'Untitled task';
  const description = clean(task.description);
  const context = clean(task.context);

  const related: string[] = [];
  const goalName = clean(links.goalName);
  const workstreamName = clean(links.workstreamName);
  const serviceName = clean(links.serviceName);
  if (goalName) related.push(`goal "${goalName}"`);
  if (workstreamName) related.push(`workstream "${workstreamName}"`);
  if (serviceName) related.push(`service "${serviceName}"`);

  const parts: string[] = [
    "Given this engineering task, and based on our team's architecture/knowledge base, what changes are required to implement it?",
    `Task: ${title}.`,
  ];
  if (description) parts.push(`Details: ${description}.`);
  if (context) parts.push(`Why/context: ${context}.`);
  if (related.length > 0) parts.push(`Related: ${related.join(', ')}.`);
  parts.push(
    'Respond in Markdown: list the specific services/components/files likely to change, ' +
      'the concrete steps, risks, and testing needed. Cite sources.',
  );

  return parts.join(' ');
}

export default buildResearchQuestion;
